import type { Emotion } from '../types/emotion';
import { CRASH_IMPACT, DIZZY_DUR, type MadnessSample } from './madness';

/** Numeric pose offsets — the scene eases these, so every stage change blends smoothly */
export interface MadOffsets {
  x: number;
  y: number;
  z: number;
  pitch: number;
  yaw: number;
  roll: number;
  led: number;
  shake: number;
  /** 0 = eyes open, 1 = narrowed to slits */
  squint: number;
}

export interface MadPose extends MadOffsets {
  /** Side plates (1 = tucked in), or null to leave them to the brain */
  arms: number | null;
  emotion: Emotion;
  /** Where the eyes look (-1..1, y up), or null to leave them to the brain */
  gaze: { x: number; y: number } | null;
  /** How much the head turns with the eyes (0 = eyes only) */
  headFollow: number;
}

export const MAD_OFFSET_KEYS: Array<keyof MadOffsets> = ['x', 'y', 'z', 'pitch', 'yaw', 'roll', 'led', 'shake', 'squint'];

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const smooth = (v: number) => v * v * (3 - 2 * v);
const easeIn = (v: number) => v * v * v;
const easeOut = (v: number) => 1 - Math.pow(1 - v, 3);
const USER = { x: 0, y: 0 };

/** Mad sequence beats (s) */
const SNAPS_FROM = 0.8;
const SNAP_CYCLE = 1.4; // button half, you half
const SNAPS = 3;
const SQUINT_AT = SNAPS_FROM + SNAP_CYCLE * SNAPS; // 5.0
const APPROACH_AT = SQUINT_AT + 1.0; // 6.0
const CLOSE_AT = APPROACH_AT + 1.4; // 7.4
const SIGNAL_AT = CLOSE_AT + 1.2; // 8.6
/** One "click it!" gesture: eyes slide over → head follows and tilts → nods → eyes back → head back */
const SIGNAL_CYCLE = 2.6;
/** Close-up spot (relative to the screen centre): right up against the glass, filling nearly the whole
 *  view — only the top-right corner (CONTACT) is left free */
const CLOSE = { x: -0.24, y: -0.12, z: 3.4 };

/** Eased 0→1 ramp between two times */
const ramp = (v: number, from: number, to: number) => smooth(clamp01((v - from) / (to - from)));

type Key = [number, number, number, number, number, number, (v: number) => number];

/**
 * Target pose for the current madness stage.
 * @param pointer cursor in -1..1 (y up)
 * @param home where he currently rests — close-up and crash are placed relative to the screen centre
 */
export function madnessPose(
  m: MadnessSample,
  t: number,
  pointer: { x: number; y: number },
  home: { x: number; y: number },
): MadPose | null {
  // Direction of CONTACT from the middle of the screen, kept inside the eyes' range
  const btn = { x: Math.max(-1, Math.min(1, m.contact.x * 0.95)), y: Math.max(-1, Math.min(1, m.contact.y * 0.95)) };
  const closeX = -home.x + CLOSE.x;
  const closeY = -home.y + CLOSE.y;

  if (m.stage === 'excited') {
    // Delighted: a lively bounce and sway, plates pumping, eyes on CONTACT
    return {
      x: Math.sin(t * 3.6) * 0.08,
      y: 0.08 + Math.abs(Math.sin(t * 6.2)) * 0.12,
      z: 0.18,
      pitch: -0.08 + Math.sin(t * 6.2) * 0.05,
      yaw: Math.sin(t * 3.1) * 0.26,
      roll: Math.sin(t * 5.1) * 0.1,
      led: 0.9,
      shake: 0,
      squint: 0,
      arms: 0.3 + 0.3 * Math.sin(t * 6.2),
      emotion: 'happy',
      gaze: { x: pointer.x * 0.8, y: pointer.y * 0.8 },
      headFollow: 0.6,
    };
  }

  if (m.stage === 'pissed') {
    // Stage 1: annoyed — leans in a little more, and a little tenser, the closer you get to CONTACT
    const p = m.proximity;
    return {
      x: pointer.x * 0.12 * p,
      y: pointer.y * 0.08 * p,
      z: 0.35 * p,
      pitch: 0.08 * p,
      yaw: 0,
      roll: Math.sin(t * 1.3) * 0.03 + Math.sin(t * 31) * 0.008 * p,
      led: 0.3 + 0.9 * p,
      shake: 0,
      squint: 0.25 * p,
      arms: 0.2 + 0.3 * p,
      emotion: p > 0.55 ? 'angry' : 'sad',
      gaze: { x: pointer.x, y: pointer.y },
      headFollow: 0.6,
    };
  }

  if (m.stage === 'mad' && m.madT >= 0) {
    const mt = m.madT;
    const pose: MadPose = {
      x: 0,
      y: 0.04,
      z: 0.4,
      pitch: 0.1,
      yaw: 0,
      roll: 0,
      led: 1,
      shake: 0,
      squint: 0,
      arms: 0.5,
      emotion: 'angry',
      gaze: USER,
      headFollow: 0,
    };
    const mix = (a: { x: number; y: number }, b: { x: number; y: number }, k: number) => ({
      x: a.x + (b.x - a.x) * k,
      y: a.y + (b.y - a.y) * k,
    });
    /** Head pose toward a direction: turn, look up/down, and a clear cock of the head into the turn */
    const headTo = (dir: { x: number; y: number }, amount: number) => {
      pose.yaw = dir.x * 0.62 * amount;
      pose.pitch = 0.1 - dir.y * 0.42 * amount;
      pose.roll = -dir.x * 0.22 * amount;
    };
    const cursor = { x: pointer.x, y: pointer.y };
    /**
     * Natural look-over: eyes lead (k across 0..0.3 of the move), the head follows a beat later and
     * overshoots a touch before settling — like a person turning to stare at something.
     */
    const lookOver = (from: { x: number; y: number }, to: { x: number; y: number }, c: number, len: number, amount: number) => {
      const eyes = ramp(c, 0, len * 0.18);
      const head = ramp(c, len * 0.08, len * 0.5);
      const settle = Math.sin(clamp01((c - len * 0.3) / (len * 0.35)) * Math.PI) * 0.12;
      pose.gaze = mix(from, to, eyes);
      const dir = mix(from, to, head);
      headTo(dir, amount * (1 + settle));
    };

    if (mt < SNAPS_FROM) {
      // Hard stare at you
      pose.gaze = USER;
      headTo(USER, 1);
    } else if (mt < SQUINT_AT) {
      // Button → you, three times: the eyes flick first, the head turns after and cocks toward it
      const c = (mt - SNAPS_FROM) % SNAP_CYCLE;
      const half = SNAP_CYCLE / 2;
      if (c < half) lookOver(USER, btn, c, half, 1.15);
      else lookOver(btn, USER, c - half, half, 1.15);
      pose.z = 0.5;
      pose.led = 1.4;
    } else if (mt < APPROACH_AT) {
      // Eyes narrow to slits, glaring at you
      pose.gaze = USER;
      headTo(USER, 1);
      pose.squint = smooth(clamp01((mt - SQUINT_AT) / 0.5));
      pose.z = 0.3;
      pose.pitch = 0.18;
      pose.led = 1.6;
    } else if (mt < SIGNAL_AT) {
      // Moves right up to the glass — fills the view except the CONTACT corner — and tracks your cursor
      const k = smooth(clamp01((mt - APPROACH_AT) / (CLOSE_AT - APPROACH_AT)));
      pose.x = closeX * k;
      pose.y = 0.04 + (closeY - 0.04) * k;
      pose.z = 0.3 + (CLOSE.z - 0.3) * k;
      pose.squint = 1;
      pose.gaze = { x: pointer.x, y: pointer.y };
      pose.yaw = pointer.x * 0.12;
      pose.pitch = 0.08 - pointer.y * 0.08;
      pose.roll = Math.sin(t * 27) * 0.006;
      pose.led = 1.8;
      pose.arms = 0.75;
    } else {
      // "Click it." — a slow, deliberate gesture, repeated:
      //  0.00–0.35  eyes slide from your cursor to CONTACT (head still)
      //  0.25–0.95  head follows: turns, tips up and cocks clearly toward the button, body leans after it
      //  0.95–1.55  two pointed nods at the button, eyes widening a little
      //  1.55–1.85  eyes slide back to you
      //  1.75–2.45  head turns back, squints, stares you down
      pose.x = closeX;
      pose.y = closeY;
      pose.z = CLOSE.z;
      pose.arms = 0.75;
      const c = (mt - SIGNAL_AT) % SIGNAL_CYCLE;
      const eyesOut = ramp(c, 0, 0.35) * (1 - ramp(c, 1.55, 1.85));
      const headOut = ramp(c, 0.25, 0.95) * (1 - ramp(c, 1.75, 2.45));
      const nodWin = c > 0.95 && c < 1.55 ? Math.sin(((c - 0.95) / 0.6) * Math.PI * 2) : 0;
      const nod = Math.max(0, nodWin);
      // Eyes: from your cursor to the button (overshooting slightly to the far edge of their range)
      pose.gaze = mix(cursor, { x: btn.x * 1.05, y: btn.y * 1.05 }, eyesOut);
      // Head: from following you softly to a strong, cocked turn at CONTACT
      const base = { x: cursor.x * 0.2, y: cursor.y * 0.15 };
      const dir = mix(base, btn, headOut);
      headTo(dir, 1 + 0.15 * headOut);
      pose.pitch -= nod * 0.16 * btn.y; // nods *toward* the button
      pose.roll -= nod * 0.05 * Math.sign(btn.x || 1);
      // Body leans a touch after the head
      pose.x += btn.x * 0.07 * headOut + btn.x * 0.025 * nod;
      pose.y += btn.y * 0.05 * headOut + btn.y * 0.02 * nod;
      pose.squint = 1 - 0.5 * headOut;
      pose.led = 1.8 + headOut * 0.6 + nod * 0.8;
    }
    return pose;
  }

  if (m.stage === 'psychic' && m.crashT >= 0) {
    // Stage 2: from the close-up he backs off deep into the scene → tremble → rush the glass → bounce off → home
    const ct = m.crashT;
    const cx = -home.x;
    const cy = -home.y;
    const keys: Key[] = [
      [0, closeX, closeY, CLOSE.z, 0.1, 0, smooth],
      [0.35, closeX * 0.8, closeY * 0.8, CLOSE.z + 0.3, 0.2, 0, easeOut], // last warning lunge
      [1.1, 0, 0.5, -9, -0.1, 0, smooth], // back off into the background
      [1.5, 0, 0.5, -9, -0.1, 0, smooth], // charging up
      [CRASH_IMPACT, cx, cy, 5, 0, 0, easeIn], // rush straight at the glass
      [2.4, cx * 0.6, cy * 0.6 - 0.4, 1.2, 0.3, 0.5, easeOut], // bounce off
      [3.6, 0, -0.05, 0, 0.1, 0, smooth], // wobble home
    ];
    let i = 1;
    while (i < keys.length - 1 && ct > keys[i][0]) i++;
    const a = keys[i - 1];
    const b = keys[i];
    const k = b[6](clamp01((ct - a[0]) / (b[0] - a[0])));
    const lerp = (j: number) => a[j] + (b[j] - a[j]) * k;

    const charging = ct > 0.9 && ct < CRASH_IMPACT ? smooth(clamp01((ct - 0.9) / 0.6)) : 0;
    const impact = ct >= CRASH_IMPACT && ct < 2.3 ? 1 - (ct - CRASH_IMPACT) / (2.3 - CRASH_IMPACT) : 0;
    const dazed = ct >= 2.3 ? 1 - clamp01((ct - 2.3) / 1.3) : 0;

    return {
      x: lerp(1) + Math.sin(t * 70) * 0.05 * charging,
      y: lerp(2) + Math.sin(t * 63) * 0.04 * charging,
      z: lerp(3),
      pitch: lerp(4),
      yaw: Math.sin(t * 9) * 0.15 * dazed,
      roll: lerp(5) * Math.sin(t * 7) + Math.sin(t * 6) * 0.2 * dazed,
      led: ct < CRASH_IMPACT ? 1.5 + 1.5 * charging : -0.5 * dazed,
      shake: Math.max(0.25 * charging, impact),
      squint: ct < CRASH_IMPACT ? 0.8 : 0.4 * dazed,
      arms: ct < CRASH_IMPACT ? 0.7 + 0.3 * charging : null,
      emotion: ct < CRASH_IMPACT ? 'angry' : ct < 2.3 ? 'surprised' : 'sleepy',
      gaze: ct < CRASH_IMPACT ? USER : { x: Math.cos(t * 4) * 0.7, y: Math.sin(t * 4) * 0.5 },
      headFollow: 0.3,
    };
  }

  if (m.stage === 'dizzy' && m.dizzyT >= 0) {
    // Nausea: eyes rolling in circles, head lolling, body swaying and sagging; occasional queasy lurch.
    // Strong at first, fading out over ~20s.
    const d = 1 - smooth(clamp01(m.dizzyT / DIZZY_DUR));
    const lurchPhase = (m.dizzyT % 4.3) / 4.3;
    const lurch = lurchPhase > 0.82 ? Math.sin(((lurchPhase - 0.82) / 0.18) * Math.PI) : 0;
    const roll = 3.2 - 1.4 * (1 - d); // eyes slow down as he recovers
    return {
      x: Math.sin(t * 1.1) * 0.14 * d,
      y: (Math.sin(t * 2.2) * 0.05 - 0.12 - lurch * 0.12) * d,
      z: 0,
      pitch: (0.12 + lurch * 0.25) * d,
      yaw: Math.sin(t * 0.9) * 0.22 * d,
      roll: (Math.sin(t * 1.7) * 0.28 + Math.sin(t * 0.6) * 0.1) * d,
      led: -0.6 * d,
      shake: 0,
      squint: (0.35 + lurch * 0.4) * d,
      arms: d > 0.15 ? 0.25 + lurch * 0.5 : null,
      emotion: d > 0.55 ? 'sleepy' : d > 0.2 ? (lurch > 0.2 ? 'sad' : 'sleepy') : 'neutral',
      gaze: { x: Math.cos(t * roll) * 0.75 * d, y: Math.sin(t * roll) * 0.55 * d },
      headFollow: 0.35,
    };
  }

  return null;
}
